import { useMemo, useState } from 'react';
import { charactersList } from '@/mocks/characters';
import { useSeo } from '@/hooks/useSeo';
import PageBanner from '@/components/base/PageBanner';
import CharacterCard from '@/components/base/CharacterCard';
import Pagination from '@/components/base/Pagination';
import Reveal from '@/components/base/Reveal';
import { ELEMENTS } from '@/utils/elements';

const ELEMENT_OPTIONS = [
  { value: 'all', label: '全部元素', icon: 'ri-apps-line' },
  { value: 'anemo', label: '风', icon: ELEMENTS.anemo.icon },
  { value: 'pyro', label: '火', icon: ELEMENTS.pyro.icon },
  { value: 'hydro', label: '水', icon: ELEMENTS.hydro.icon },
  { value: 'electro', label: '雷', icon: ELEMENTS.electro.icon },
  { value: 'geo', label: '岩', icon: ELEMENTS.geo.icon },
  { value: 'cryo', label: '冰', icon: ELEMENTS.cryo.icon },
  { value: 'dendro', label: '草', icon: ELEMENTS.dendro.icon },
];

const ROLE_OPTIONS = [
  { value: 'all', label: '全部定位' },
  { value: 'dps', label: '输出' },
  { value: 'support', label: '辅助' },
  { value: 'defense', label: '防御' },
];

const SORT_OPTIONS = [
  { value: 'popularity', label: '按人气排序' },
  { value: 'date', label: '按发布时间排序' },
];

const PAGE_SIZE = 8;

export default function Characters() {
  useSeo({
    title: '原神角色攻略大全 – 原神官方游戏站',
    description: '原神全角色攻略大全，按元素与定位筛选，包含角色培养思路、武器圣遗物搭配与实战配队建议。',
    keywords: '原神角色,原神角色攻略,角色培养,角色配队,元素定位',
    canonicalPath: '/characters',
  });

  const [element, setElement] = useState('all');
  const [role, setRole] = useState('all');
  const [sort, setSort] = useState('popularity');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return charactersList
      .filter((item) => (element === 'all' ? true : item.element === element))
      .filter((item) => (role === 'all' ? true : item.role === role))
      .sort((a, b) => {
        if (sort === 'date') return a.releaseDate < b.releaseDate ? 1 : -1;
        return b.popularity - a.popularity;
      });
  }, [element, role, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setElement('all');
    setRole('all');
    setSort('popularity');
    setPage(1);
  };

  return (
    <div>
      <PageBanner
        kicker="CHARACTER GUIDE"
        title="角色攻略大全"
        subtitle="按元素与角色定位筛选，查看每位角色的培养优先级、技能解析与配队推荐。"
        crumbs={[{ label: '首页', to: '/' }, { label: '角色攻略' }]}
        image={charactersList[0].splash}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
        <div className="mb-8 space-y-5 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div>
            <p className="mb-2.5 text-xs font-label text-foreground-500">按元素筛选</p>
            <div className="flex flex-wrap gap-2">
              {ELEMENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setElement(option.value);
                    setPage(1);
                  }}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-label transition-colors ${
                    element === option.value
                      ? 'border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  <i className={`${option.icon} text-sm`}></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="mb-2.5 text-xs font-label text-foreground-500">按定位筛选</p>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setRole(option.value);
                      setPage(1);
                    }}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                      role === option.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              <div className="flex rounded-full bg-background-100 p-1">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSort(option.value)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-label transition-colors ${
                      sort === option.value ? 'bg-background-50 text-primary-700' : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="mb-5 text-xs text-foreground-500">
          共找到 <span className="font-medium text-primary-700">{filtered.length}</span> 位角色
        </p>

        {paged.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {paged.map((item, index) => (
              <Reveal key={item.id} delay={index * 50}>
                <CharacterCard item={item} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-user-search-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">没有匹配的角色</p>
            <p className="mt-1.5 text-sm text-foreground-500">换个元素或定位条件再试试看。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}